import React, { useState, useEffect } from 'react';
import {
  Box, Container, Paper, Typography, Button, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Chip, CircularProgress, Grid, Alert, IconButton,
  Snackbar, Stepper, Step, StepLabel,
  Divider, FormControl, InputLabel, Select, MenuItem, Card, CardContent,
  Stack, Fade, Skeleton, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Badge, Tooltip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import EventIcon from '@mui/icons-material/Event';
import DownloadIcon from '@mui/icons-material/Download';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import PrintIcon from '@mui/icons-material/Print';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const HolidayManagement = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [holidayList, setHolidayList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeStep, setActiveStep] = useState(0);
  const [inputMode, setInputMode] = useState('manual');
  const [viewMode, setViewMode] = useState('list');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [fromUpload, setFromUpload] = useState(false);

  const [formData, setFormData] = useState({
    year: new Date().getFullYear(),
    title: '',
    description: '',
    created_by: '',
    holidays: []
  });

  const [singleHoliday, setSingleHoliday] = useState({
    date: '',
    name: '',
    type: 'public',
    description: '',
    is_optional: false
  });

  const [file, setFile] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [companyId, setCompanyId] = useState('');
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const steps = ['Input Method', 'Holiday Details'];

  const holidayTypes = {
    public: { label: 'Public Holiday', color: '#ef4444', bgColor: '#fef2f2' },
    national: { label: 'National Holiday', color: '#3b82f6', bgColor: '#eff6ff' },
    religious: { label: 'Religious Holiday', color: '#a855f7', bgColor: '#faf5ff'},
    optional: { label: 'Optional Holiday', color: '#f59e0b', bgColor: '#fffbeb' },
    company: { label: 'Company Holiday', color: '#06b6d4', bgColor: '#ecfeff' }
  };

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  useEffect(() => {
    fetchCompanyId();
  }, []);

  useEffect(() => {
    if (companyId) {
      fetchHolidayList();
    }
  }, [companyId]);

  const fetchCompanyId = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setCompanyId(payload.company_id);
        const createdBy = payload.name || payload.email || 'HR Admin';
        setFormData(prev => ({ ...prev, created_by: createdBy }));
      } else {
        setError('Authentication token not found. Please log in again.');
      }
    } catch (err) {
      //console.error('Error fetching company ID:', err);
      setError('Failed to authenticate. Please log in again.');
    }
  };

  const fetchHolidayList = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/holidays`,  {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
      if (res.data.holidayList) {
        setHolidayList(res.data.holidayList);
        if (fromUpload) {
          setViewMode('calendar');
          setFromUpload(false);
        }
      } else {
        setHolidayList(null);
      }
    } catch (err) {
      //console.error('Error fetching holiday list:', err);
      const errorMsg = err.response?.data?.error || 'Failed to fetch holiday list';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const loadHolidayToForm = (list) => {
    setFormData({
      year: list.year,
      title: list.title || '',
      description: list.description || '',
      created_by: list.created_by || formData.created_by,
      holidays: list.holidays ? JSON.parse(list.holidays) : []
    });
    setIsEditMode(true);
  };

  const handleGeneralChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSingleHolidayChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSingleHoliday(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const addHoliday = () => {
    if (!singleHoliday.date || !singleHoliday.name) {
      showSnackbar('Please enter date and holiday name', 'error');
      return;
    }

    const newHoliday = { ...singleHoliday };
    setFormData(prev => ({
      ...prev,
      holidays: [...prev.holidays, newHoliday].sort((a, b) => new Date(a.date) - new Date(b.date))
    }));

    setSingleHoliday({
      date: '',
      name: '',
      type: 'public',
      description: '',
      is_optional: false
    });
    showSnackbar('Holiday added successfully', 'success');
  };

  const removeHoliday = (index) => {
    setFormData(prev => ({
      ...prev,
      holidays: prev.holidays.filter((_, i) => i !== index)
    }));
    showSnackbar('Holiday removed', 'info');
  };

const handleFileChange = async (e) => {
  const selectedFile = e.target.files[0];
  if (!selectedFile) return;

  // Updated valid types to include more Excel formats
  const validTypes = [
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/csv',
    'application/csv',
    'text/plain' // For CSV files
  ];
  
  // Also check file extension as fallback
  const validExtensions = ['.xlsx', '.xls', '.csv'];
  const fileExtension = selectedFile.name.substring(selectedFile.name.lastIndexOf('.')).toLowerCase();
  
  if (!validTypes.includes(selectedFile.type) && !validExtensions.includes(fileExtension)) {
    showSnackbar('Please upload an Excel file (.xlsx, .xls) or CSV file', 'error');
    e.target.value = '';
    return;
  }

  if (selectedFile.size > 10 * 1024 * 1024) {
    showSnackbar('File size must be less than 10MB', 'error');
    e.target.value = '';
    return;
  }

  setFile(selectedFile);
  showSnackbar(`File "${selectedFile.name}" selected. Click "Extract & Continue" to process.`, 'info');
};

const handleDownloadTemplate = async () => {
  const year = new Date().getFullYear();
  
  try {
    // Import xlsx library dynamically (you need to install it first: npm install xlsx)
    const XLSX = await import('xlsx');
    
    // Create sample data
    const sampleData = [
      { Date: `${year}-01-26`, Name: 'Republic Day', Type: 'national', Description: 'National holiday celebrating the Constitution', Optional: 'No' },
      { Date: `${year}-08-15`, Name: 'Independence Day', Type: 'national', Description: 'Independence from British rule', Optional: 'No' },
      { Date: `${year}-10-02`, Name: 'Gandhi Jayanti', Type: 'national', Description: 'Birth anniversary of Mahatma Gandhi', Optional: 'No' },
      { Date: `${year}-12-25`, Name: 'Christmas', Type: 'religious', Description: 'Christian festival', Optional: 'No' },
      { Date: `${year}-01-01`, Name: "New Year's Day", Type: 'public', Description: 'Beginning of the calendar year', Optional: 'No' }
    ];

    // Create worksheet
    const ws = XLSX.utils.json_to_sheet(sampleData);
    
    // Add instructions as comments or separate sheet
    const instructions = [
      ['INSTRUCTIONS FOR HOLIDAY UPLOAD:'],
      [''],
      ['1. Date Format: YYYY-MM-DD (e.g., 2024-12-25) or DD/MM/YYYY (e.g., 25/12/2024)'],
      ['2. Valid Types: public, national, religious, optional, company'],
      ['3. Optional: Enter "Yes" or "No"'],
      ['4. Keep the header row (Date, Name, Type, Description, Optional)'],
      ['5. Delete example rows or add your own holidays below'],
      ['6. All fields except Description are required'],
      [''],
      ['Note: You can add as many rows as needed. Duplicate dates will be handled automatically.']
    ];

    // Create a second sheet for instructions
    const wsInstructions = XLSX.utils.aoa_to_sheet(instructions);
    
    // Create workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Holidays');
    XLSX.utils.book_append_sheet(wb, wsInstructions, 'Instructions');
    
    // Set column widths for better readability
    ws['!cols'] = [
      { wch: 15 }, // Date
      { wch: 25 }, // Name
      { wch: 15 }, // Type
      { wch: 40 }, // Description
      { wch: 12 }  // Optional
    ];

    // Generate Excel file
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    
    // Create download
    const blob = new Blob([wbout], { type: 'application/octet-stream' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Holiday_Template_${year}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    showSnackbar('Excel template downloaded! Fill in your holidays and upload.', 'success');
    
  } catch (error) {
    //console.error('Template download error:', error);
    // Fallback to CSV if xlsx library is not available
    showSnackbar('Failed to create Excel template. Please install xlsx library.', 'error');
    
    // Fallback CSV option
    const csvContent = `Date,Name,Type,Description,Optional
${year}-01-26,Republic Day,national,National holiday celebrating the Constitution,No
${year}-08-15,Independence Day,national,Independence from British rule,No
${year}-10-02,Gandhi Jayanti,national,Birth anniversary of Mahatma Gandhi,No
${year}-12-25,Christmas,religious,Christian festival,No
${year}-01-01,New Year's Day,public,Beginning of the calendar year,No

INSTRUCTIONS:
- Date Format: YYYY-MM-DD (e.g. 2024-12-25) or DD/MM/YYYY (e.g. 25/12/2024)
- Valid Types: public, national, religious, optional, company
- Optional: Enter "Yes" or "No"
- Keep the header row (Date,Name,Type,Description,Optional)
- Delete example rows or add your own holidays below
`;

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Holiday_Template_${year}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    showSnackbar('CSV template downloaded as fallback. For best results, use Excel format.', 'info');
  }
};

  const handleExtractFields = async () => {
    if (!file) {
      showSnackbar('Please select a file first', 'error');
      return;
    }

    setExtracting(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const formDataToSend = new FormData();
      formDataToSend.append('holidayFile', file);

      const res = await axios.post(
        `${API_URL}/api/holidays/extract`,
        formDataToSend,
        { withCredentials: true,
          headers:{
            Authorization: `Bearer ${token}`
          }
         }
      );

      if (res.data.success && res.data.holidays) {
        setFormData(prev => ({
          ...prev,
          year: res.data.year || prev.year,
          title: res.data.title || prev.title,
          description: res.data.description || prev.description,
          holidays: res.data.holidays
        }));
        setFromUpload(true);
        showSnackbar(`âœ… ${res.data.holidays.length} holidays extracted! Review and submit.`, 'success');
        setActiveStep(1);
      }
    } catch (err) {
      //console.error('Extraction error:', err);
      const errorMsg = err.response?.data?.details || err.response?.data?.error || 'Failed to extract holidays';
      showSnackbar(errorMsg, 'error');
      setError(errorMsg);
    } finally {
      setExtracting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      year: new Date().getFullYear(),
      title: '',
      description: '',
      created_by: formData.created_by,
      holidays: []
    });
    setSingleHoliday({
      date: '',
      name: '',
      type: 'public',
      description: '',
      is_optional: false
    });
    setFile(null);
    setIsEditMode(false);
    setError('');
    setActiveStep(0);
    setInputMode('manual');
    setFromUpload(false);

    const fileInput = document.getElementById('holiday-file-upload');
    if (fileInput) fileInput.value = '';
  };

  const validateForm = () => {
    if (!formData.year) {
      setError('Please select a year');
      return false;
    }
    if (!formData.title || formData.title.trim().length < 3) {
      setError('Title must be at least 3 characters');
      return false;
    }
    if (formData.holidays.length === 0) {
      setError('Please add at least one holiday');
      return false;
    }
    setError('');
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setSubmitLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const dataToSend = {
        year: formData.year,
        title: formData.title,
        description: formData.description,
        created_by: formData.created_by,
        holidays: JSON.stringify(formData.holidays)
      };

      const url = isEditMode
        ? `${API_URL}/api/holidays/${holidayList.id}`
        : `${API_URL}/api/holidays/create`;

      await (isEditMode ? axios.put : axios.post)(url, dataToSend,  {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      showSnackbar(`Holiday list ${isEditMode ? 'updated' : 'created'} successfully!`, 'success');
      setOpenDialog(false);
      resetForm();
      fetchHolidayList();
    } catch (err) {
      //console.error('Submit Error:', err);
      const errorMsg = err.response?.data?.error || err.response?.data?.details || 'Failed to save holiday list';
      setError(errorMsg);
      showSnackbar(errorMsg, 'error');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this holiday list? This action cannot be undone.')) return;
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API_URL}/api/holidays/${holidayList.id}`,  {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
      showSnackbar('Holiday list deleted successfully', 'success');
      setHolidayList(null);
      resetForm();
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to delete holiday list';
      showSnackbar(errorMsg, 'error');
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Professional Calendar Rendering
  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month, year) => {
    return new Date(year, month, 1).getDay();
  };

  const getHolidaysForDate = (day, month, year) => {
    if (!holidayList || !holidayList.holidays) return [];
    const holidays = JSON.parse(holidayList.holidays);
    return holidays.filter(h => {
      const holidayDate = new Date(h.date + 'T00:00:00');
      return holidayDate.getDate() === day &&
             holidayDate.getMonth() === month &&
             holidayDate.getFullYear() === year;
    });
  };

  const handlePrevMonth = () => {
    if (selectedMonth === 0) {
      setSelectedMonth(11);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (selectedMonth === 11) {
      setSelectedMonth(0);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  const handleToday = () => {
    const today = new Date();
    setSelectedMonth(today.getMonth());
    setSelectedYear(today.getFullYear());
  };

  const handlePrint = () => {
    if (!holidayList || !holidayList.holidays) return;
    const holidays = JSON.parse(holidayList.holidays);
    
    const printWindow = window.open('', '_blank');
    const title = holidayList.title || `Company Holiday List ${holidayList.year}`;
    const year = holidayList.year;
    
    const rowsHtml = holidays.map((h, index) => {
      const date = new Date(h.date + 'T00:00:00');
      const formattedDate = date.toLocaleDateString(undefined, {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
      const dayOfWeek = date.toLocaleDateString(undefined, { weekday: 'long' });
      const typeLabel = holidayTypes[h.type]?.label || h.type || 'Public Holiday';
      const typeColor = holidayTypes[h.type]?.color || '#3b82f6';
      const typeBg = holidayTypes[h.type]?.bgColor || '#eff6ff';

      return `
        <tr>
          <td style="padding: 10px 8px; border-bottom: 1px solid #e2e8f0; text-align: center; font-size: 12px;">${index + 1}</td>
          <td style="padding: 10px 8px; border-bottom: 1px solid #e2e8f0; font-weight: 600; white-space: nowrap; font-size: 12px;">${formattedDate}</td>
          <td style="padding: 10px 8px; border-bottom: 1px solid #e2e8f0; white-space: nowrap; font-size: 12px;">${dayOfWeek}</td>
          <td style="padding: 10px 8px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #1e293b; font-size: 12px;">${h.name}</td>
          <td style="padding: 10px 8px; border-bottom: 1px solid #e2e8f0; white-space: nowrap; font-size: 12px;">
            <span style="display: inline-block; white-space: nowrap; padding: 4px 10px; border-radius: 12px; font-size: 11px; font-weight: 600; background-color: ${typeBg}; color: ${typeColor}; border: 1px solid ${typeColor}; text-align: center;">
              ${typeLabel}
            </span>
          </td>
          <td style="padding: 10px 8px; border-bottom: 1px solid #e2e8f0; font-size: 12px;">${h.description || '-'}</td>
        </tr>
      `;
    }).join('');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${title}</title>
          <style>
            @page {
              size: A4 portrait;
              margin: 12mm 15mm;
            }
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
              margin: 0;
              padding: 15px;
              color: #1e293b;
              background: #fff;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            .header {
              text-align: center;
              padding-bottom: 12px;
              border-bottom: 3px solid #667eea;
              margin-bottom: 16px;
            }
            .header h1 {
              margin: 0 0 4px 0;
              color: #2d3748;
              font-size: 22px;
              font-weight: 700;
            }
            .header p {
              margin: 0;
              color: #718096;
              font-size: 13px;
            }
            .meta-bar {
              display: flex;
              justify-content: space-between;
              align-items: center;
              background-color: #f8fafc;
              padding: 8px 14px;
              border-radius: 6px;
              margin-bottom: 16px;
              font-size: 12px;
              color: #475569;
              border: 1px solid #e2e8f0;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              table-layout: fixed;
              margin-top: 5px;
            }
            th {
              background-color: #ebf8ff;
              color: #2b6cb0;
              font-weight: 700;
              text-align: left;
              padding: 10px 8px;
              border-bottom: 2px solid #bee3f8;
              font-size: 12px;
              text-transform: uppercase;
              letter-spacing: 0.5px;
            }
            th:first-child {
              text-align: center;
            }
            td {
              vertical-align: middle;
              word-wrap: break-word;
            }
            .footer {
              margin-top: 24px;
              text-align: center;
              font-size: 11px;
              color: #a0aec0;
              border-top: 1px solid #e2e8f0;
              padding-top: 12px;
            }
            @media print {
              body { padding: 0; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>${title}</h1>
            <p>Official Holiday Calendar - Year ${year}</p>
          </div>
          <div class="meta-bar">
            <span><strong>Total Holidays:</strong> ${holidays.length}</span>
            <span><strong>Created By:</strong> ${holidayList.created_by || 'HR'}</span>
            <span><strong>Printed Date:</strong> ${new Date().toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })}</span>
          </div>
          <table>
            <thead>
              <tr>
                <th style="width: 35px; text-align: center;">#</th>
                <th style="width: 110px; white-space: nowrap;">Date</th>
                <th style="width: 100px; white-space: nowrap;">Day</th>
                <th style="width: 170px;">Holiday Name</th>
                <th style="width: 140px; white-space: nowrap;">Type</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              ${rowsHtml}
            </tbody>
          </table>
          <div class="footer">
            <p>PulseHR - Organization Holiday Calendar</p>
          </div>
          <script>
            window.onload = () => {
              window.print();
              window.onafterprint = () => window.close();
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(selectedMonth, selectedYear);
    const firstDay = getFirstDayOfMonth(selectedMonth, selectedYear);
    const today = new Date();
    const isCurrentMonth = today.getMonth() === selectedMonth && today.getFullYear() === selectedYear;
    
    const days = [];
    const totalCells = Math.ceil((daysInMonth + firstDay) / 7) * 7;

    // Empty cells before month starts
    for (let i = 0; i < firstDay; i++) {
      days.push(
        <Box
          key={`empty-${i}`}
          sx={{
            aspectRatio: '1',
            minHeight: 100,
            border: '1px solid',
            borderColor: 'grey.100',
            bgcolor: 'grey.50'
          }}
        />
      );
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(selectedYear, selectedMonth, day);
      const dayHolidays = getHolidaysForDate(day, selectedMonth, selectedYear);
      const isToday = isCurrentMonth && day === today.getDate();
      const isWeekend = date.getDay() === 0 || date.getDay() === 6;
      const hasHoliday = dayHolidays.length > 0;

      days.push(
        <Tooltip
          key={day}
          title={
            dayHolidays.length > 0 ? (
              <Box>
                {dayHolidays.map((h, idx) => (
                  <Box key={idx} sx={{ mb: idx < dayHolidays.length - 1 ? 1 : 0 }}>
                    <Typography variant="caption" fontWeight={600}>
                      {holidayTypes[h.type]?.icon} {h.name}
                    </Typography>
                    {h.description && (
                      <Typography variant="caption" display="block" sx={{ opacity: 0.8 }}>
                        {h.description}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            ) : ''
          }
          arrow
          placement="top"
        >
          <Box
            sx={{
              aspectRatio: '1',
              minHeight: 100,
              border: '1px solid',
              borderColor: isToday ? 'primary.main' : hasHoliday ? 'error.200' : 'grey.200',
              borderWidth: isToday ? 2 : 1,
              bgcolor: hasHoliday ? 'error.50' : isWeekend ? 'grey.50' : 'white',
              p: 1,
              position: 'relative',
              transition: 'all 0.2s',
              cursor: hasHoliday ? 'pointer' : 'default',
              '&:hover': {
                bgcolor: hasHoliday ? 'error.100' : isWeekend ? 'grey.100' : 'grey.50',
                transform: hasHoliday ? 'scale(1.02)' : 'none',
                zIndex: hasHoliday ? 10 : 1,
                boxShadow: hasHoliday ? 3 : 0
              }
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
              <Badge
                badgeContent={dayHolidays.length}
                color="error"
                invisible={dayHolidays.length === 0}
                sx={{
                  '& .MuiBadge-badge': {
                    fontSize: '0.6rem',
                    height: 16,
                    minWidth: 16,
                    padding: '0 4px'
                  }
                }}
              >
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: isToday ? '50%' : 1,
                    bgcolor: isToday ? 'primary.main' : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Typography
                    variant="body2"
                    fontWeight={isToday ? 700 : isWeekend ? 600 : 400}
                    color={isToday ? 'white' : isWeekend ? 'text.secondary' : 'text.primary'}
                    sx={{ fontSize: '0.875rem' }}
                  >
                    {day}
                  </Typography>
                </Box>
              </Badge>
            </Box>

            <Box sx={{ mt: 1 }}>
              {dayHolidays.slice(0, 2).map((holiday, idx) => (
                <Chip
                  key={idx}
                  label={holiday.name}
                  size="small"
                  sx={{
                    width: '100%',
                    height: 22,
                    fontSize: '0.65rem',
                    mb: 0.5,
                    bgcolor: holidayTypes[holiday.type]?.bgColor,
                    color: holidayTypes[holiday.type]?.color,
                    border: '1px solid',
                    borderColor: holidayTypes[holiday.type]?.color,
                    fontWeight: 600,
                    '& .MuiChip-label': {
                      px: 0.75,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }
                  }}
                  icon={
                    <span style={{ fontSize: '0.75rem', marginLeft: 4 }}>
                      {holidayTypes[holiday.type]?.icon}
                    </span>
                  }
                />
              ))}
              {dayHolidays.length > 2 && (
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    textAlign: 'center',
                    color: 'text.secondary',
                    fontSize: '0.65rem',
                    fontWeight: 600
                  }}
                >
                  +{dayHolidays.length - 2} more
                </Typography>
              )}
            </Box>
          </Box>
        </Tooltip>
      );
    }

    // Empty cells after month ends
    const remainingCells = totalCells - days.length;
    for (let i = 0; i < remainingCells; i++) {
      days.push(
        <Box
          key={`empty-end-${i}`}
          sx={{
            aspectRatio: '1',
            minHeight: 100,
            border: '1px solid',
            borderColor: 'grey.100',
            bgcolor: 'grey.50'
          }}
        />
      );
    }

    return (
      <Box>
        {/* Calendar Header */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 2,
            bgcolor: 'primary.50',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'primary.200'
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <IconButton onClick={handlePrevMonth} size="small" sx={{ bgcolor: 'white' }}>
              <ChevronLeftIcon />
            </IconButton>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h5" fontWeight={700} color="primary.main">
                {months[selectedMonth]} {selectedYear}
              </Typography>
              <IconButton onClick={handleToday} size="small" sx={{ bgcolor: 'white' }}>
                <TodayIcon fontSize="small" />
              </IconButton>
            </Box>
            
            <IconButton onClick={handleNextMonth} size="small" sx={{ bgcolor: 'white' }}>
              <ChevronRightIcon />
            </IconButton>
          </Box>
        </Paper>

        {/* Legend */}
        <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
          {Object.entries(holidayTypes).map(([key, val]) => (
            <Chip
              key={key}
              label={val.label}
              size="small"
              icon={<span>{val.icon}</span>}
              sx={{
                bgcolor: val.bgColor,
                color: val.color,
                border: '1px solid',
                borderColor: val.color,
                fontWeight: 600
              }}
            />
          ))}
        </Box>

        {/* Day Headers */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0, mb: 0 }}>
          {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day, idx) => (
            <Box
              key={day}
              sx={{
                p: 1.5,
                bgcolor: idx === 0 || idx === 6 ? 'error.50' : 'primary.50',
                border: '1px solid',
                borderColor: 'grey.200',
                textAlign: 'center'
              }}
            >
              <Typography
                variant="body2"
                fontWeight={700}
                color={idx === 0 || idx === 6 ? 'error.main' : 'primary.main'}
                sx={{ fontSize: '0.75rem' }}
              >
                {day}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Calendar Grid */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0 }}>
          {days}
        </Box>

        {/* Footer Stats */}
        <Paper elevation={0} sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            <strong>
              {holidayList ? JSON.parse(holidayList.holidays).filter(h => {
                const d = new Date(h.date + 'T00:00:00');
                return d.getMonth() === selectedMonth && d.getFullYear() === selectedYear;
              }).length : 0}
            </strong> holidays in {months[selectedMonth]} {selectedYear}
          </Typography>
        </Paper>
      </Box>
    );
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box sx={{ py: 2 }}>
            <Typography variant="h6" gutterBottom fontWeight={600} sx={{ mb: 3 }}>
              Choose how you'd like to create your holiday list
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card
                  sx={{
                    height: '100%',
                    cursor: 'pointer',
                    border: 2,
                    borderColor: inputMode === 'manual' ? 'primary.main' : 'grey.200',
                    transition: 'all 0.3s',
                    '&:hover': {
                      boxShadow: 6,
                      transform: 'translateY(-4px)'
                    }
                  }}
                  onClick={() => { setInputMode('manual'); setActiveStep(1); }}
                >
                  <CardContent sx={{ p: 4, textAlign: 'center' }}>
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: '50%',
                        bgcolor: 'primary.50',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 16px'
                      }}
                    >
                      <EditIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                    </Box>
                    <Typography variant="h6" gutterBottom fontWeight={600}>
                      Manual Entry
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Add holidays one by one with complete details and customization
                    </Typography>
                    <Chip
                      label="Best for custom lists"
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card
                  sx={{
                    height: '100%',
                    cursor: 'pointer',
                    border: 2,
                    borderColor: inputMode === 'upload' ? 'secondary.main' : 'grey.200',
                    transition: 'all 0.3s',
                    '&:hover': {
                      boxShadow: 6,
                      transform: 'translateY(-4px)'
                    }
                  }}
                  onClick={() => setInputMode('upload')}
                >
                  <CardContent sx={{ p: 4, textAlign: 'center' }}>
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: '50%',
                        bgcolor: 'secondary.50',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 16px'
                      }}
                    >
                      <UploadFileIcon sx={{ fontSize: 32, color: 'secondary.main' }} />
                    </Box>
                    <Typography variant="h6" gutterBottom fontWeight={600}>
                      Excel Upload
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Upload your Excel file and automatically extract all holidays
                    </Typography>
                    <Chip
                      label="Fastest way to import"
                      size="small"
                      color="secondary"
                      variant="outlined"
                    />

                    {inputMode === 'upload' && (
                      <Fade in={true}>
                        <Box sx={{ mt: 3, textAlign: 'left' }}>
                          <Stack spacing={2}>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<DownloadIcon />}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDownloadTemplate();
                              }}
                              fullWidth
                            >
                              Download Excel Template
                            </Button>

                            <Box>
                              <input
                                type="file"
                                accept=".xls,.xlsx"
                                onChange={handleFileChange}
                                style={{ display: 'none' }}
                                id="holiday-file-upload"
                              />
                              <Button
                                variant="outlined"
                                component="label"
                                htmlFor="holiday-file-upload"
                                fullWidth
                                startIcon={<UploadFileIcon />}
                              >
                                {file ? `ðŸ“„ ${file.name.substring(0, 25)}${file.name.length > 25 ? '...' : ''}` : 'Choose Excel File'}
                              </Button>
                            </Box>

                            {file && (
                              <Button
                                variant="contained"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleExtractFields();
                                }}
                                fullWidth
                                disabled={extracting}
                                startIcon={extracting ? <CircularProgress size={20} /> : null}
                              >
                                {extracting ? 'Extracting Holidays...' : 'Extract & Continue'}
                              </Button>
                            )}

<Alert severity="info" sx={{ mt: 1 }}>
  <Typography variant="caption">
    <strong>Supported:</strong> Excel files (.xlsx, .xls) and CSV files with columns: Date, Name, Type, Description, Optional
    <br />
    <strong>Download</strong> the template above to get started.
  </Typography>
</Alert>
                          </Stack>
                        </Box>
                      </Fade>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        );

      case 1:
        return (
          <Box sx={{ py: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Paper sx={{ p: 3, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.100' }}>
                  <Typography variant="subtitle2" fontWeight={600} color="primary.main" gutterBottom>
                    Basic Information
                  </Typography>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Year"
                        name="year"
                        type="number"
                        value={formData.year}
                        onChange={handleGeneralChange}
                        fullWidth
                        required
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} md={8}>
                      <TextField
                        label="Title"
                        name="title"
                        value={formData.title}
                        onChange={handleGeneralChange}
                        fullWidth
                        required
                        placeholder="e.g., Company Holiday List 2024"
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Description"
                        name="description"
                        value={formData.description}
                        onChange={handleGeneralChange}
                        fullWidth
                        multiline
                        rows={2}
                        size="small"
                        placeholder="Brief description of this holiday list"
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }}>
                  <Chip label="Add Holidays" color="primary" />
                </Divider>
              </Grid>

              <Grid item xs={12}>
                <Paper sx={{ p: 3, bgcolor: 'grey.50' }}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ mb: 2 }}>
                    Add New Holiday
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                      <TextField
                        label="Date"
                        name="date"
                        type="date"
                        value={singleHoliday.date}
                        onChange={handleSingleHolidayChange}
                        fullWidth
                        required
                        size="small"
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <TextField
                        label="Holiday Name"
                        name="name"
                        value={singleHoliday.name}
                        onChange={handleSingleHolidayChange}
                        fullWidth
                        required
                        size="small"
                        placeholder="e.g., Independence Day"
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Type</InputLabel>
                        <Select
                          name="type"
                          value={singleHoliday.type}
                          label="Type"
                          onChange={handleSingleHolidayChange}
                        >
                          {Object.entries(holidayTypes).map(([key, val]) => (
                            <MenuItem key={key} value={key}>
                              {val.icon} {val.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Description"
                        name="description"
                        value={singleHoliday.description}
                        onChange={handleSingleHolidayChange}
                        fullWidth
                        size="small"
                        placeholder="Brief description of the holiday"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        <Button
                          variant="contained"
                          onClick={addHoliday}
                          startIcon={<AddIcon />}
                        >
                          Add Holiday
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {formData.holidays.length > 0 && (
                <Grid item xs={12}>
                  <Paper sx={{ p: 3 }}>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ mb: 2 }}>
                      Holidays List ({formData.holidays.length})
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Date</TableCell>
                            <TableCell>Holiday Name</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell align="center">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {formData.holidays.map((holiday, index) => (
                            <TableRow key={index}>
                              <TableCell>
                                {new Date(holiday.date + 'T00:00:00').toLocaleDateString(undefined, {
                                  day: '2-digit',
                                  month: 'short',
                                  year: 'numeric'
                                })}
                              </TableCell>
                              <TableCell>{holiday.name}</TableCell>
                              <TableCell>
                                <Chip
                                  label={holidayTypes[holiday.type]?.label}
                                  size="small"
                                  sx={{
                                    bgcolor: holidayTypes[holiday.type]?.bgColor,
                                    color: holidayTypes[holiday.type]?.color,
                                    border: '1px solid',
                                    borderColor: holidayTypes[holiday.type]?.color
                                  }}
                                />
                              </TableCell>
                              <TableCell>{holiday.description || '-'}</TableCell>
                              <TableCell align="center">
                                <IconButton
                                  size="small"
                                  onClick={() => removeHoliday(index)}
                                  color="error"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                </Grid>
              )}
            </Grid>
          </Box>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2, mb: 3 }} />
          <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} sx={{ py: 4, width: '100%' }}>
      {/* Header Section */}
      <Paper
        elevation={0}
        sx={{
          p: 4,
          mb: 4,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          borderRadius: 3
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Holiday Details
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Manage and view your organization's holiday calendar
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            {holidayList && (
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={handlePrint}
                size="large"
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.15)',
                  color: 'white',
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.25)', borderColor: 'white' },
                  fontWeight: 600,
                  px: 3
                }}
              >
                Print
              </Button>
            )}
            <Button
              variant="contained"
              startIcon={holidayList ? <EditIcon /> : <AddIcon />}
              onClick={() => {
                if (holidayList) {
                  loadHolidayToForm(holidayList);
                  setIsEditMode(true);
                } else {
                  resetForm();
                  setIsEditMode(false);
                }
                setOpenDialog(true);
              }}
              size="large"
              sx={{
                bgcolor: 'white',
                color: '#667eea',
                '&:hover': { bgcolor: 'grey.100' },
                fontWeight: 600,
                px: 4
              }}
            >
              {holidayList ? 'Edit Holiday List' : 'Create Holiday List'}
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert
            severity="error"
            sx={{ mt: 3, bgcolor: 'rgba(255,255,255,0.95)' }}
            onClose={() => setError('')}
          >
            {error}
          </Alert>
        )}
      </Paper>

      {/* Holiday Display Section */}
      {holidayList ? (
        <Fade in={true}>
          <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
            <Box
              sx={{
                p: 3,
                bgcolor: 'success.50',
                borderBottom: '1px solid',
                borderColor: 'success.200'
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      bgcolor: 'success.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <CalendarMonthIcon sx={{ color: 'white', fontSize: 28 }} />
                  </Box>
                  <Box>
                    <Chip
                      label={`YEAR ${holidayList.year}`}
                      color="success"
                      size="small"
                      sx={{ fontWeight: 600, mb: 1 }}
                    />
                    <Typography variant="h5" fontWeight={700} color="text.primary">
                      {holidayList.title}
                    </Typography>
                    {holidayList.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {holidayList.description}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <IconButton
                    onClick={() => {
                      loadHolidayToForm(holidayList);
                      setIsEditMode(true);
                      setOpenDialog(true);
                    }}
                    sx={{
                      bgcolor: 'white',
                      '&:hover': { bgcolor: 'primary.50' }
                    }}
                    size="small"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    onClick={handleDelete}
                    sx={{
                      bgcolor: 'white',
                      '&:hover': { bgcolor: 'error.50', color: 'error.main' }
                    }}
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Box>

              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Total Holidays:</strong> {JSON.parse(holidayList.holidays).length}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Created By:</strong> {holidayList.created_by}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            {/* View Toggle */}
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'grey.200', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant={viewMode === 'list' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => setViewMode('list')}
                  startIcon={<EventIcon />}
                >
                  List View
                </Button>
                <Button
                  variant={viewMode === 'calendar' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => setViewMode('calendar')}
                  startIcon={<CalendarMonthIcon />}
                >
                  Calendar View
                </Button>
              </Box>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                onClick={handlePrint}
                startIcon={<PrintIcon />}
              >
                Print
              </Button>
            </Box>

            {/* Content */}
            <Box sx={{ p: 3 }}>
              {viewMode === 'list' ? (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Day</TableCell>
                        <TableCell>Holiday Name</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Description</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {JSON.parse(holidayList.holidays).map((holiday, index) => {
                        const date = new Date(holiday.date + 'T00:00:00');
                        return (
                          <TableRow key={index}>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>
                                {date.toLocaleDateString(undefined, {
                                  day: '2-digit',
                                  month: 'short',
                                  year: 'numeric'
                                })}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {date.toLocaleDateString(undefined, { weekday: 'long' })}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>
                                {holiday.name}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={holidayTypes[holiday.type]?.label}
                                size="small"
                                icon={<span>{holidayTypes[holiday.type]?.icon}</span>}
                                sx={{
                                  bgcolor: holidayTypes[holiday.type]?.bgColor,
                                  color: holidayTypes[holiday.type]?.color,
                                  border: '1px solid',
                                  borderColor: holidayTypes[holiday.type]?.color
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              {holiday.description || '-'}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                renderCalendar()
              )}
            </Box>
          </Paper>
        </Fade>
      ) : (
        <Fade in={true}>
          <Paper
            elevation={2}
            sx={{
              p: 8,
              textAlign: 'center',
              borderRadius: 3,
              border: '2px dashed',
              borderColor: 'grey.300'
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                bgcolor: 'grey.100',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px'
              }}
            >
              <CalendarMonthIcon sx={{ fontSize: 40, color: 'grey.400' }} />
            </Box>
            <Typography variant="h5" fontWeight={600} gutterBottom color="text.secondary">
              No Holiday List Found
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 500, mx: 'auto' }}>
              Create your organization's holiday calendar for the year
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              onClick={() => {
                resetForm();
                setOpenDialog(true);
                setIsEditMode(false);
              }}
              sx={{ px: 4, py: 1.5, fontWeight: 600 }}
            >
              Create Holiday List
            </Button>
          </Paper>
        </Fade>
      )}

      {/* Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => { setOpenDialog(false); resetForm(); }}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h5" fontWeight={700}>
            {isEditMode ? 'Edit' : 'Create'} Holiday List
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {isEditMode ? 'Update your holiday calendar' : 'Set up holidays for the year'}
          </Typography>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 4 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {!isEditMode && (
            <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          )}

          {renderStepContent()}
        </DialogContent>

        <DialogActions sx={{ px: 4, py: 2.5, borderTop: '1px solid', borderColor: 'divider', gap: 1 }}>
          {activeStep > 0 && !isEditMode && (
            <Button
              onClick={() => setActiveStep(prev => prev - 1)}
              variant="outlined"
            >
              Back
            </Button>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Button
            onClick={() => { setOpenDialog(false); resetForm(); }}
            disabled={submitLoading}
            variant="outlined"
          >
            Cancel
          </Button>
          {activeStep === 1 && (
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={submitLoading || !formData.title || formData.holidays.length === 0}
              startIcon={submitLoading ? <CircularProgress size={20} /> : null}
              sx={{ px: 4, fontWeight: 600 }}
            >
              {submitLoading ? 'Saving...' : (isEditMode ? 'Update List' : 'Create List')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default HolidayManagement;